import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  TextField,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import { Close, Settings } from '@mui/icons-material'
import { SelectChangeEvent } from '@mui/material/Select'
import { DatePickerMui } from 'src/components/DatePickerMui'
import { DivergenceFilters } from '../../DivergenceDemandsTypes'
import { selectDivergenceFilterOptions } from '../../DivergenceDemandsSelector'
import dayjs, { Dayjs } from 'dayjs'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

interface LocalFilters {
  status?: string[]
  floor?: string | null
  initial_date?: string
  final_date?: string
  cod_prod?: string
  level?: string | null
  street?: string
  shed?: string
  type?: string | null
}

interface DivergenceFiltersModalProps {
  open: boolean
  onClose: () => void
  filters: DivergenceFilters
  onApplyFilters: (filters: DivergenceFilters) => void
}

export const DivergenceFiltersModal: React.FC<DivergenceFiltersModalProps> = ({
  open,
  onClose,
  filters,
  onApplyFilters,
}) => {
  const [localFilters, setLocalFilters] = useState<LocalFilters>({})
  const filterOptions = useSelector(selectDivergenceFilterOptions)

  useEffect(() => {
    const mappedFilters: LocalFilters = {
      status: filters.status?.map((statusNum) => {
        const statusOption = filterOptions?.status.find((opt) => opt.value === statusNum)
        return statusOption?.label || 'Criada'
      }) || [],
      floor: filterOptions?.floor.find((opt) => opt.value === filters.floor)?.label || null,
      initial_date: filters.initial_date,
      final_date: filters.final_date,
      cod_prod: filters.cod_prod,
      level: filterOptions?.level.find((opt) => opt.value === filters.level)?.label || null,
      street: filters.street,
      shed: filters.shed,
      type: filterOptions?.type.find((opt) => opt.value === filters.type)?.label || null,
    }
    setLocalFilters(mappedFilters)
  }, [filters, filterOptions])

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target
    setLocalFilters({
      ...localFilters,
      status: typeof value === 'string' ? value.split(',') : value,
    })
  }

  const handleFloorChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target
    setLocalFilters({
      ...localFilters,
      floor: value === '' ? null : value,
    })
  }

  const handleDataInicialChange = (date?: Dayjs) => {
    setLocalFilters({
      ...localFilters,
      initial_date: date?.format('YYYY-MM-DD'),
    })
  }

  const handleDataFinalChange = (date?: Dayjs) => {
    setLocalFilters({
      ...localFilters,
      final_date: date?.format('YYYY-MM-DD'),
    })
  }

  const handleCodigoProdutoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters({
      ...localFilters,
      cod_prod: event.target.value,
    })
  }

  const handleLevelChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target
    setLocalFilters({
      ...localFilters,
      level: value === 'Todos' ? null : value,
    })
  }

  const handleShedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters({
      ...localFilters,
      shed: event.target.value,
    })
  }

  const handleStreetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalFilters({
      ...localFilters,
      street: event.target.value,
    })
  }

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setLocalFilters({
      ...localFilters,
      type: event.target.value,
    })
  }

  const handleApply = () => {
    const typeOption = filterOptions?.type.find((opt) => opt.label === localFilters.type)
    const floorOption = filterOptions?.floor.find((opt) => opt.label === localFilters.floor)

    const mappedFilters: DivergenceFilters = {
      status: localFilters.status?.map((statusText) => {
        const statusOption = filterOptions?.status.find((opt) => opt.label === statusText)
        return statusOption?.value || 1
      }) || [],
      level: filterOptions?.level.find((opt) => opt.label === localFilters.level)?.value || null,
      floor: floorOption?.value !== undefined ? floorOption.value : null,
      initial_date: localFilters.initial_date,
      final_date: localFilters.final_date,
      cod_prod: localFilters.cod_prod,
      street: localFilters.street,
      shed: localFilters.shed,
      type: typeOption?.value !== undefined ? typeOption.value : null,
    }
    onApplyFilters(mappedFilters)
    onClose()
  }

  const handleClear = () => {
    const clearedFilters: LocalFilters = {}
    setLocalFilters(clearedFilters)
    const clearedApiFilters: DivergenceFilters = {}
    onApplyFilters(clearedApiFilters)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box display='flex' alignItems='center' gap={1}>
            <Settings />
            <Typography variant='h6'>Filtros de Divergências</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth size='small'>
              <InputLabel id='status-label'>Status</InputLabel>
              <Select
                labelId='status-label'
                id='status-select'
                multiple
                value={localFilters.status || []}
                onChange={handleStatusChange}
                input={<OutlinedInput label='Status' />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size='small' />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {filterOptions?.status.map((option) => (
                  <MenuItem key={option.value} value={option.label}>
                    {option.label}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth size='small'>
              <InputLabel id='floor-label'>Piso</InputLabel>
              <Select
                labelId='floor-label'
                id='floor-select'
                value={localFilters.floor ?? ''}
                onChange={handleFloorChange}
                label='Piso'
              >
                {filterOptions?.floor.map((option, index) => (
                  <MenuItem key={`floor-${option.label}-${index}`} value={option.label}>
                    {option.label}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth size='small'>
              <InputLabel id='level-label'>Nível</InputLabel>
              <Select
                labelId='level-label'
                id='level-select'
                value={localFilters.level ?? (filterOptions?.level.find((opt) => opt.value === null)?.label || 'Todos')}
                onChange={handleLevelChange}
                label='Nível'
              >
                {filterOptions?.level.map((option, index) => (
                  <MenuItem key={`level-${option.label}-${index}`} value={option.label}>
                    {option.label}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <DatePickerMui
              label='Data Inicial'
              currentValue={localFilters.initial_date ? dayjs(localFilters.initial_date) : undefined}
              setCurrentValue={handleDataInicialChange}
              size='small'
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <DatePickerMui
              label='Data Final'
              currentValue={localFilters.final_date ? dayjs(localFilters.final_date) : undefined}
              setCurrentValue={handleDataFinalChange}
              size='small'
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              size='small'
              label='Código do Produto'
              value={localFilters.cod_prod || ''}
              onChange={handleCodigoProdutoChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              size='small'
              label='Galpão'
              value={localFilters.shed || ''}
              onChange={handleShedChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <TextField
              fullWidth
              size='small'
              label='Rua'
              value={localFilters.street || ''}
              onChange={handleStreetChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} lg={4}>
            <FormControl fullWidth size='small'>
              <InputLabel id='type-label'>Tipo</InputLabel>
              <Select
                labelId='type-label'
                id='type-select'
                value={localFilters.type || (filterOptions?.type.find((opt) => opt.value === null)?.label || '')}
                onChange={handleTypeChange}
                label='Tipo'
              >
                {filterOptions?.type.map((option, index) => (
                  <MenuItem key={`type-${option.label}-${index}`} value={option.label}>
                    {option.label}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} color='secondary'>
          Limpar Filtros
        </Button>
        <Button onClick={handleApply} variant='contained' color='primary'>
          Aplicar Filtros
        </Button>
      </DialogActions>
    </Dialog>
  )
}
